import {useContext, useRef, useState} from 'react';
import {RequestACallVerificationCodeEndpointResponseClientError} from '@stripe-internal/conversations-shared-dependencies';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {ConfigContext} from '../../../Providers/ConfigProvider';
import {useApi} from '../../../Providers/ApiProvider/ApiProvider';
import {useMountEffect} from '../../../useMountEffect';

type VerificationCodes = {
  agentCode: string;
  userCode: string;
};

const POLLING_INTERVAL_IN_S = 15;
// poll for 1 hour
const POLLING_LIMIT = (60 * 60) / POLLING_INTERVAL_IN_S;

// Fetches verification codes every 15 seconds
export function useRacVerificationCodes() {
  const {fetchVerificationCodes} = useApi();
  const analytics = useClientAnalytics();

  const {audience} = useContext(ConfigContext);
  const [verificationCodes, setVerificationCodes] = useState<
    VerificationCodes | undefined
  >();
  const [loading, setLoading] = useState<boolean>(true);
  const [clientError, setClientError] = useState<
    RequestACallVerificationCodeEndpointResponseClientError | undefined
  >();
  const [serverError, setServerError] = useState<boolean>(false);

  const intervalRef = useRef<NodeJS.Timeout>();
  const pollCountRef = useRef<number>(0);

  useMountEffect(() => {
    if (intervalRef.current) {
      return;
    }

    const fetchCodes = async () => {
      if (pollCountRef.current >= POLLING_LIMIT) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
        // setting a server error if this somehow happens,
        // so we don't show stale pins
        setServerError(true);
        return;
      }

      pollCountRef.current += 1;
      analytics.track('support_conversations_rac_pins_refresh');

      try {
        const resp = await fetchVerificationCodes(audience);
        if (resp.type === 'success') {
          setVerificationCodes({
            agentCode: resp.agentCode,
            userCode: resp.userCode,
          });
          setClientError(undefined);
          setServerError(false);
        } else {
          setClientError(resp);
        }
      } catch {
        setServerError(true);
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      } finally {
        setLoading(false);
      }
    };

    fetchCodes();

    intervalRef.current = setInterval(fetchCodes, POLLING_INTERVAL_IN_S * 1000);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    };
  });

  return {clientError, serverError, loading, verificationCodes};
}
